import React, {useEffect, useState} from "react";
import {Reservation} from "types/entities/Reservation";
import {ReservationPlan} from "types/entities/ReservationPlan";
import {useDispatch} from "useDispatch";
import {useTranslation} from "react-i18next";
import {calculateReservationPlanTimepickerHoursAndMinutes} from "utils/reservationUtils";
import {toast} from "react-toastify";
import dayjs from "dayjs";
import SellerAction from "store/seller/SellerAction";
import {CheckIcon, PlusIcon} from "@heroicons/react/24/solid";
import ReservationPlanListRow
    from "pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerPaymentPlan/components/ReservationPlanListRow";
import UiButton, {UiButtonSize, UiButtonStyle} from "pages/Front/components/Ui/components/UiButton";
import clsx from "clsx";
import BookingCard from "pages/Front/Booking/components/BookingCard";
import {CloudArrowDownIcon} from "@heroicons/react/24/outline";
import {useHistory} from "react-router-dom";
import {downloadHandler, getFilenameFromLink} from "utils/sellerUtils";

interface BookingSellerPaymentPlanProps {
    reservation: Reservation;
    className?: string;
}

const BookingSellerPaymentPlan = (props: BookingSellerPaymentPlanProps) => {
    const {t} = useTranslation();
    const history = useHistory();
    const dispatch = useDispatch();
    const [reservationPlans, setReservationPlans] = useState<ReservationPlan[]>(props.reservation.extra?.reservation_plan);
    const reservationStartTime = props.reservation.date_time ?? dayjs(props.reservation.date).hour(props.reservation.hour).minute(props.reservation.minute).toISOString();
    const [lastUsedHour, setLastUsedHour] = useState<number>(0);
    const startHourBefore = 0;
    const minuteIntervals = 30;
    const timepickerSettings = calculateReservationPlanTimepickerHoursAndMinutes(reservationStartTime, startHourBefore, minuteIntervals);

    const addNewPlanHandler = () => {
        const newPlan: ReservationPlan = {
            id: reservationPlans ? reservationPlans.length + 1 : 1,
            time: (lastUsedHour * 1800) + (timepickerSettings.defaultHour) * 3600,
            name: "",
            is_active: false,
        };
        setLastUsedHour((lastHour: number) => lastHour + 1);
        setReservationPlans(prevState => [...prevState, newPlan]);
    };

    const updatePlanHandler = (newPlan: ReservationPlan) => {
        const planIndex = reservationPlans.findIndex(plan => plan.id === newPlan.id);
        const newPlans = {...reservationPlans};
        newPlans[planIndex] = newPlan;
        setReservationPlans(newPlans);
    };

    const savePlansHandler = () => {
        const filledReservationPlans = reservationPlans.filter((plan) => plan.time !== 0 || plan.name !== "");
        dispatch(SellerAction.fetchSetReservationPlan(props.reservation.id, filledReservationPlans)).then(r => {
            toast.success(t("Reservation plans has been created."));
        }).catch(err => {
            toast.error(t("Reservation plan create failed."));
            if (err?.status && err?.status === 401) {
                history.replace("/booking/seller/login");
            }
        })
    };

    const getPdfHandler = () => {
        dispatch(SellerAction.fetchGetReservationPlan(props.reservation.id)).then(r => {
            downloadHandler(r, getFilenameFromLink(r)).then(() => {});
            toast.success(t("Download PDF"));
        }).catch(err => {
            if (err?.status && err?.status === 401) {
                history.replace("/booking/seller/login");
            }
        });
    };

    useEffect(() => {
        if (props.reservation.extra && props.reservation.extra.reservation_plan) {
            setReservationPlans(props.reservation.extra.reservation_plan);
        }
    }, [props.reservation?.extra?.reservation_plan]);

    return (
        <BookingCard className={clsx("BookingSellerPaymentPlan !px-0 !py-0 self-baseline", props.className)}>
            <h2 className={clsx("BookingCardTitle font-bold text-xl text-black px-3 py-2.5 xl:px-3 xl:py-3 flex items-center justify-between border-b space-x-3")}>
                <div className={"inline-flex items-center gap-x-2"}>
                    <span>{t("Plan")}</span>
                </div>

                <div className={"flex flex-1 flex-row flex-wrap gap-2 justify-between"}>
                    <UiButton
                        onClick={getPdfHandler}
                        title={t("Download PDF")}
                        icon={<CloudArrowDownIcon className={"w-4 h-4 mr-1.5"} />}
                        style={UiButtonStyle.Print}
                        size={UiButtonSize.Small}
                    />

                    <div className={"flex flex-row flex-wrap gap-2"}>
                        <UiButton
                            onClick={addNewPlanHandler}
                            title={t("Add new item")}
                            icon={<PlusIcon className={"w-4 h-4 mr-1.5"} />}
                            style={UiButtonStyle.Add}
                            size={UiButtonSize.Small}
                        />

                        <UiButton
                            onClick={savePlansHandler}
                            title={t("Save plan")}
                            icon={<CheckIcon className={"w-4 h-4 mr-1.5"} />}
                            size={UiButtonSize.Small}
                        />
                    </div>
                </div>
            </h2>
            <div className={"ReservationPlanItems space-y-4 px-3 py-3 bg-gray-50"}>
                {reservationPlans.length > 0 &&
                    reservationPlans.map((plan, index) => {
                        return <ReservationPlanListRow key={plan.id} plan={plan} reservation={props.reservation} onChange={() => updatePlanHandler} />;
                    })}
                {reservationPlans.length === 0 && <p className={"text-center"}>{t("No plan has been created")}</p>}
            </div>
        </BookingCard>
    );
};

export default BookingSellerPaymentPlan;